import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "Accueil", to: "/" },
  { label: "Trouver un emploi", to: "/jobs" },
  { label: "Trouver un candidat", to: "/talent" },
  { label: "Réseau", to: "/reseau" },
  { label: "Marché", to: "/marche" },
  { label: "Business", to: "/business" },
  { label: "Portefeuille", to: "/portefeuille" },
  { label: "Objectifs", to: "/objectifs" },
  { label: "Assistant IA", to: "/ia-activite" },
  { label: "Entreprises", to: "/#employeurs" },
  { label: "Candidats", to: "/#candidats" },
  { label: "Ressources", to: "/#ressources" },
  { label: "À propos", to: "/#apropos" },
];

const LCS_ROLES = ["super_admin", "admin_lcs", "recruteur_lcs", "assistant_recruteur"];

export default function PublicNav() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  let navItems = NAV_ITEMS;
  if (user) {
    navItems = [
      ...NAV_ITEMS.slice(0, 6),
      { label: "Publicité", to: "/publicite" },
      { label: "Paramètres", to: "/parametres" },
      ...NAV_ITEMS.slice(6),
    ];
    if (LCS_ROLES.includes(user.role)) {
      navItems = [...navItems.slice(0, 6), { label: "Recrutement", to: "/recrutement" }, ...navItems.slice(6)];
    }
  }

  const handleAnchor = (e, to) => {
    if (!to.includes("#")) {
      setMenuOpen(false);
      return;
    }
    const id = to.split("#")[1];
    setMenuOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `/#${id}`);
    }
  };

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" aria-label="NEXA — Accueil">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-6" aria-label="Navigation principale">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={(e) => handleAnchor(e, item.to)}
              className="text-sm font-medium text-brand-navy/70 hover:text-brand-turquoise transition-colors duration-200"
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden text-brand-navy p-2 -mr-2"
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          {user ? (
            <Link
              data-testid="nav-dashboard-link"
              to="/dashboard"
              className="text-sm font-semibold text-white bg-brand-navy rounded-lg px-5 py-2.5 hover:bg-brand-blue transition-colors duration-200"
            >
              Mon espace
            </Link>
          ) : (
            <>
              <Link
                data-testid="nav-login-link"
                to="/login"
                className="text-sm font-medium text-brand-navy hover:text-brand-blue transition-colors duration-200"
              >
                Connexion
              </Link>
              <Link
                data-testid="nav-register-link"
                to="/register"
                className="text-sm font-semibold text-white bg-brand-turquoise rounded-lg px-5 py-2.5 hover:bg-brand-navy transition-colors duration-200"
              >
                Créer un compte
              </Link>
            </>
          )}
        </div>
      </div>
      {menuOpen && (
        <nav data-testid="mobile-menu" className="lg:hidden border-t border-slate-100 bg-white px-6 py-4 flex flex-col gap-3" aria-label="Navigation mobile">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={(e) => handleAnchor(e, item.to)}
              className="text-sm font-medium text-brand-navy/80 hover:text-brand-turquoise"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
